import { BoldLabelText } from '@/components/BoldLabelText/BoldLabelText';
import { DescriptionCol, DescriptionRow } from '@/components/Description';
import { useErrorInlineNotification } from '@/components/ErrorInlineNotification';
import { TransactionItemDateFormatType } from '@/context/services/transaction_items/List/TransactionItems.service';
import {
  ComponentType,
  TransactionItemDrawerMode,
  TransactionItemDrawerModeEnum,
  TransactionItemFormType,
} from '@/features/transaction_items/List/type';
import { Date } from '@/features/transaction_items/components/Form/Date';
import { ComponentTypeSelect } from '@/features/transaction_items/components/Form/GeneralForm/ComponentTypeSelect';
import { LabelInput } from '@/features/transaction_items/components/Form/GeneralForm/LabelInput';
import { RequiredSettingCheckbox } from '@/features/transaction_items/components/Form/GeneralForm/RequiredSettingCheckbox';
import { VisibilityCheckbox } from '@/features/transaction_items/components/Form/GeneralForm/VisibilityCheckbox';
import { Numeric } from '@/features/transaction_items/components/Form/Numeric';
import { Pulldown } from '@/features/transaction_items/components/Form/Pulldown';
import { TextAndAlphanumeric } from '@/features/transaction_items/components/Form/TextAndAlphanumeric';
import { useSubmitCreateTransactionItem } from '@/features/transaction_items/components/Form/hooks/useSubmitCreateTransactionItem';
import { useSubmitUpdateTransactionItem } from '@/features/transaction_items/components/Form/hooks/useSubmitUpdateTransactionItem';
import { useTranslation } from '@/i18n';
import {
  Form,
  Label,
  Loading,
  useToastAPI,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import {
  FC,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { ComponentPreview } from './ComponentPreview';
import styles from './TransactionItemGeneralForm.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  mode: TransactionItemDrawerMode;
  isActiveCustomItemCountLimitEnabled: boolean;
  showLoading: (loading: boolean) => void;
  isLoading: boolean;
  closeDrawer: () => void;
  selectedDetailId?: string;
  defaultValues?: TransactionItemFormType;
  onShowUpdateConfirm: (show: boolean) => void;
  onSetMode: (mode: TransactionItemDrawerMode) => void;
  handleSuccess: () => Promise<void>;
  notificationErrorContextHolder?: ReactNode;
  notificationErrorVisible?: boolean;
  visibleCustomItemsInUseCount?: number;
  visibleCountServerErrorMessage?: string | null;
  onVisibleCountServerError?: (message: string) => void;
  onClearVisibleCountServerError?: () => void;
  itemType?: string;
};

export const ComponentTypeMap = {
  pulldown: Pulldown,
  text: TextAndAlphanumeric,
  alphanumeric: TextAndAlphanumeric,
  numeric: Numeric,
  date: Date,
};

export const TEXT_MAX_CHARS = 255;
export const ALPHANUMERIC_MAX_CHARS = 1000;
export const SUPPORTED_DECIMAL_PLACES = 3;
export const MAX_NUMERIC_STRING_LENGTH = 35;

type SectionKey =
  | 'label'
  | 'customizable'
  | 'visibility'
  | 'componentTypeSelect'
  | 'required'
  | 'component';

const DEFAULT_VALUES: TransactionItemFormType = {
  label: '',
  is_visible: false,
  is_required: false,
  component_type: undefined,
  min_chars: 0,
  max_chars: 0,
  min_value: undefined,
  max_value: undefined,
  placeholder: '',
  tooltip_message: '',
  date_format_type: undefined,
};

export const TransactionItemGeneralForm: FC<Props> = memo(
  ({
    mode,
    isActiveCustomItemCountLimitEnabled,
    showLoading,
    isLoading,
    closeDrawer,
    defaultValues,
    onShowUpdateConfirm,
    selectedDetailId,
    onSetMode,
    handleSuccess: _handleSuccess,
    notificationErrorContextHolder,
    notificationErrorVisible,
    visibleCustomItemsInUseCount = 0,
    visibleCountServerErrorMessage = null,
    onVisibleCountServerError,
    onClearVisibleCountServerError,
    itemType,
  }) => {
    const method = useForm<TransactionItemFormType>({
      mode: 'onChange',
      defaultValues: defaultValues ?? DEFAULT_VALUES,
    });

    const {
      setValue,
      clearErrors,
      control,
      reset,
      formState: { isDirty },
    } = method;

    const isVisible = useWatch({ control, name: 'is_visible' });
    const prevIsVisibleRef = useRef<boolean | undefined>(undefined);
    useEffect(() => {
      prevIsVisibleRef.current = undefined;
    }, [selectedDetailId]);

    useEffect(() => {
      if (!isActiveCustomItemCountLimitEnabled) return;
      if (prevIsVisibleRef.current === undefined) {
        prevIsVisibleRef.current = isVisible;
        return;
      }
      const isVisibilityUnchecked =
        prevIsVisibleRef.current === true && isVisible === false;
      prevIsVisibleRef.current = isVisible;
      // エラー発生かつチェックボックスのチェックが外れた（true -> false に変化）場合に、VisibleCountのエラーメッセージをクリアする
      // Clear the visible-count error message only when an error exists and the visibility checkbox is unchecked (true -> false).
      if (isVisibilityUnchecked && visibleCountServerErrorMessage) {
        onClearVisibleCountServerError?.();
      }
    }, [
      isVisible,
      onClearVisibleCountServerError,
      isActiveCustomItemCountLimitEnabled,
      visibleCountServerErrorMessage,
    ]);

    const [componentType, setComponentType] = useState<ComponentType>(
      defaultValues?.component_type ?? undefined
    );
    const initialIsVisible = Boolean(defaultValues?.is_visible);
    const visibilityLimitApplies =
      isActiveCustomItemCountLimitEnabled &&
      mode !== TransactionItemDrawerModeEnum.DETAIL &&
      itemType !== 'basic';
    const showVisibleLimitError =
      isActiveCustomItemCountLimitEnabled &&
      Boolean(visibleCountServerErrorMessage);
    const effectiveVisibleCustomItemsInUseCount =
      isActiveCustomItemCountLimitEnabled ? visibleCustomItemsInUseCount : 0;

    useEffect(() => {
      if (mode === TransactionItemDrawerModeEnum.CREATE) {
        reset(DEFAULT_VALUES);
        setComponentType(undefined);
        return;
      }

      if (!defaultValues) return;

      reset(defaultValues);
      setComponentType(defaultValues.component_type ?? undefined);
    }, [defaultValues, mode, reset]);

    const notify = useToastAPI();
    const { t } = useTranslation();
    const [
      { setError: setVisibleLimitError },
      visibleLimitErrorContextHolder,
      VisibleLimitErrorProvider,
      clearVisibleLimitErrorMessage,
    ] = useErrorInlineNotification();

    useEffect(() => {
      if (!isActiveCustomItemCountLimitEnabled) return;
      if (visibleCountServerErrorMessage) {
        setVisibleLimitError(new Error(visibleCountServerErrorMessage));
        return;
      }
      clearVisibleLimitErrorMessage();
    }, [
      clearVisibleLimitErrorMessage,
      isActiveCustomItemCountLimitEnabled,
      setVisibleLimitError,
      visibleCountServerErrorMessage,
    ]);

    const getMaxCharsForComponentType = useCallback(
      (componentType: ComponentType): number => {
        if (componentType === 'text') return TEXT_MAX_CHARS;
        if (componentType === 'alphanumeric') return ALPHANUMERIC_MAX_CHARS;
        return 0;
      },
      []
    );

    const getDateFormatTypeForComponentType = useCallback(
      (
        componentType: ComponentType
      ): TransactionItemDateFormatType | undefined => {
        if (componentType === 'date') return 'year_month_day';
        return undefined;
      },
      []
    );

    const resetFormValuesForComponentType = useCallback(
      (componentType: ComponentType) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { label, is_visible, is_required, component_type, ...rest } =
          DEFAULT_VALUES;
        const defaultValues = {
          ...rest,
          max_chars: getMaxCharsForComponentType(componentType),
          date_format_type: getDateFormatTypeForComponentType(componentType),
        };
        Object.entries(defaultValues).forEach(([key, value]) => {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          setValue(key as keyof TransactionItemFormType, value);
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          clearErrors(key as keyof TransactionItemFormType);
        });
      },
      [
        getMaxCharsForComponentType,
        setValue,
        clearErrors,
        getDateFormatTypeForComponentType,
      ]
    );

    const onSelectComponentType = useCallback(
      (componentType: ComponentType) => {
        setComponentType(componentType);
        resetFormValuesForComponentType(componentType);
      },
      [resetFormValuesForComponentType]
    );

    const handlerSuccess = useCallback(
      async (successMsg: string) => {
        if (isActiveCustomItemCountLimitEnabled) {
          onClearVisibleCountServerError?.();
        }
        if (mode === TransactionItemDrawerModeEnum.EDIT) {
          onSetMode(TransactionItemDrawerModeEnum.DETAIL);
        } else {
          closeDrawer();
        }
        await _handleSuccess();
        notify.success({
          title: t(successMsg),
          duration: 5,
        });
      },
      [
        mode,
        _handleSuccess,
        notify,
        t,
        onSetMode,
        closeDrawer,
        isActiveCustomItemCountLimitEnabled,
        onClearVisibleCountServerError,
      ]
    );

    const handlerError = useCallback(
      (title: string) => {
        notify.error({
          title: t(title),
          duration: 5,
        });
      },
      [notify, t]
    );

    const { onSubmitCreate } = useSubmitCreateTransactionItem({
      setValidationError: method.setError,
      handlerSuccess,
      handlerError,
      showLoading,
      onServerVisibleLimitError: isActiveCustomItemCountLimitEnabled
        ? onVisibleCountServerError
        : undefined,
    });

    const { onSubmitUpdate } = useSubmitUpdateTransactionItem({
      transaction_item_id: selectedDetailId ?? '',
      setValidationError: method.setError,
      handlerSuccess,
      handlerError,
      showLoading,
      onServerVisibleLimitError: isActiveCustomItemCountLimitEnabled
        ? onVisibleCountServerError
        : undefined,
    });

    const onSubmit = useCallback(
      (data: TransactionItemFormType) => {
        switch (mode) {
          case TransactionItemDrawerModeEnum.CREATE:
            onSubmitCreate(data);
            break;
          case TransactionItemDrawerModeEnum.EDIT:
            onSubmitUpdate(data);
            break;
        }
      },
      [onSubmitCreate, onSubmitUpdate, mode]
    );

    const Component = useMemo(() => {
      if (!componentType) return null;
      if (componentType === 'date') {
        return (
          <Date
            key={componentType}
            mode={mode}
            initialIsVisible={initialIsVisible}
            visibleCustomItemsInUseCount={effectiveVisibleCustomItemsInUseCount}
            visibilityLimitApplies={visibilityLimitApplies}
            showVisibleLimitError={showVisibleLimitError}
          />
        );
      }
      const Component = ComponentTypeMap[componentType];
      return Component ? <Component key={componentType} mode={mode} /> : null;
    }, [
      componentType,
      mode,
      initialIsVisible,
      effectiveVisibleCustomItemsInUseCount,
      visibilityLimitApplies,
      showVisibleLimitError,
    ]);

    useEffect(() => {
      if (mode === TransactionItemDrawerModeEnum.EDIT) {
        onShowUpdateConfirm(isDirty);
      }
    }, [isDirty, mode, onShowUpdateConfirm]);

    const sections: Record<SectionKey, React.ReactNode> = useMemo(
      () => ({
        label: <LabelInput method={method} mode={mode} />,
        customizable: (
          <DescriptionRow>
            <DescriptionCol
              label={
                <Label testId='customizable'>
                  <BoldLabelText>
                    {t('transaction_items.table.customizable')}
                  </BoldLabelText>
                </Label>
              }
              labelWidth='176px'
            />
            <DescriptionCol
              value={<div>{t('invoice_transaction.column.customizable')}</div>}
            />
          </DescriptionRow>
        ),
        visibility: (
          <VisibilityCheckbox
            mode={mode}
            initialIsVisible={initialIsVisible}
            visibleCustomItemsInUseCount={effectiveVisibleCustomItemsInUseCount}
            visibilityLimitApplies={visibilityLimitApplies}
            showVisibleLimitError={showVisibleLimitError}
          />
        ),
        component: Component,
        componentTypeSelect: (
          <ComponentTypeSelect
            method={method}
            mode={mode}
            onChangeAfter={onSelectComponentType}
          />
        ),
        required: <RequiredSettingCheckbox mode={mode} formProps={method} />,
      }),
      [
        method,
        mode,
        t,
        onSelectComponentType,
        Component,
        initialIsVisible,
        effectiveVisibleCustomItemsInUseCount,
        visibilityLimitApplies,
        showVisibleLimitError,
      ]
    );

    const basicInputSection = useMemo(() => {
      const showCustomizable: SectionKey[] =
        mode !== TransactionItemDrawerModeEnum.CREATE ? ['customizable'] : [];

      const baseOrder: SectionKey[] = [
        'label',
        ...showCustomizable,
        'componentTypeSelect',
        'visibility',
        'component',
        'required',
      ];
      const orderByType: Partial<Record<string, SectionKey[]>> = {
        date: [
          'label',
          ...showCustomizable,
          'componentTypeSelect',
          'component',
          'required',
        ],
      };

      return orderByType[componentType ?? ''] ?? baseOrder;
    }, [componentType, mode]);

    return (
      <>
        {isLoading && <Loading size='xlarge' isOverlay color='white' />}
        <FormProvider {...method}>
          <Form<TransactionItemFormType>
            id='transaction_item_form'
            formMethod={method}
            onSubmit={onSubmit}
          >
            <div className={cx(styles['transaction-item-form'])}>
              {isActiveCustomItemCountLimitEnabled ? (
                <>
                  {visibleCountServerErrorMessage && (
                    <VisibleLimitErrorProvider value={setVisibleLimitError}>
                      <div className={cx(styles['banner-slot'])}>
                        {visibleLimitErrorContextHolder}
                      </div>
                    </VisibleLimitErrorProvider>
                  )}
                  <div className={cx(styles['form-body'])}>
                    {notificationErrorVisible && (
                      <div className={cx(styles['inline-error'])}>
                        {notificationErrorContextHolder}
                      </div>
                    )}
                    {basicInputSection.map((k) => sections[k]).filter(Boolean)}
                    {componentType && (
                      <ComponentPreview
                        componentType={componentType}
                        label={method.watch('label')}
                        placeholder={method.watch('placeholder')}
                        tooltip={method.watch('tooltip_message')}
                      />
                    )}
                  </div>
                </>
              ) : (
                <>
                  {notificationErrorVisible && (
                    <div className={cx(styles['inline-error'])}>
                      {notificationErrorContextHolder}
                    </div>
                  )}
                  {basicInputSection.map((k) => sections[k]).filter(Boolean)}
                  {componentType && (
                    <ComponentPreview
                      componentType={componentType}
                      label={method.watch('label')}
                      placeholder={method.watch('placeholder')}
                      tooltip={method.watch('tooltip_message')}
                      isRequired={method.watch('is_required')}
                    />
                  )}
                </>
              )}
            </div>
          </Form>
        </FormProvider>
      </>
    );
  }
);
TransactionItemGeneralForm.displayName = 'TransactionItemGeneralForm';
